/* ============================================================
   MoM HEADER — Memories of Motion

   The ONLY script the header needs, and it is deliberately small.

   Menus, submenus, the mobile panel and the search disclosure are all
   native <details>, so navigation works before this file loads and keeps
   working if it never does. What is left here is genuinely presentational:

     1. sticky-on-scroll-up
     2. closing an open <details> when you click outside it or press Escape,
        which browsers do not do for you

   Nothing here is required for a customer to get around the shop.
   ============================================================ */
(() => {
  class MomHeader extends HTMLElement {
    connectedCallback() {
      this.lastY = window.scrollY;
      this.sticky = this.dataset.sticky === 'true';

      if (this.sticky) {
        this.onScroll = this.onScroll.bind(this);
        window.addEventListener('scroll', this.onScroll, { passive: true });
      }

      // Click-outside and Escape for every disclosure in the header.
      this.onDocClick = this.onDocClick.bind(this);
      this.onKey = this.onKey.bind(this);
      document.addEventListener('click', this.onDocClick);
      document.addEventListener('keydown', this.onKey);

      // Only one top-level disclosure open at a time — an open search
      // panel sitting over an open menu is nobody's intention.
      this.topLevel().forEach((d) => {
        d.addEventListener('toggle', () => {
          if (!d.open) return;
          this.topLevel().forEach((other) => {
            if (other !== d) other.open = false;
          });
        });
      });
    }

    disconnectedCallback() {
      window.removeEventListener('scroll', this.onScroll);
      document.removeEventListener('click', this.onDocClick);
      document.removeEventListener('keydown', this.onKey);
    }

    topLevel() {
      return Array.from(this.querySelectorAll(':scope .mhead__mobile, :scope .mhead__search, :scope .mhead__drop'));
    }

    onScroll() {
      const y = window.scrollY;
      // Past the header's own height before anything changes, so the bar
      // does not flicker on tiny scrolls at the very top of a page.
      const past = y > this.offsetHeight;
      this.classList.toggle('is-scrolled', past);
      this.classList.toggle('is-hidden', past && y > this.lastY + 4);
      if (Math.abs(y - this.lastY) > 4) this.lastY = y;
    }

    closeAll() {
      this.topLevel().forEach((d) => (d.open = false));
    }

    onDocClick(event) {
      const open = this.topLevel().filter((d) => d.open);
      if (!open.length) return;
      open.forEach((d) => {
        if (!d.contains(event.target)) d.open = false;
      });
    }

    onKey(event) {
      if (event.key !== 'Escape') return;
      const open = this.topLevel().filter((d) => d.open);
      if (!open.length) return;
      // Return focus to the summary that opened it, or Escape leaves the
      // keyboard stranded where the panel used to be.
      const summary = open[open.length - 1].querySelector('summary');
      this.closeAll();
      if (summary) summary.focus();
    }
  }

  if (!customElements.get('mom-header')) customElements.define('mom-header', MomHeader);
})();
